import { MouseEventHandler, useCallback, useState, useEffect } from "react";
import { Pagination } from "../pagination/Pagination";
import { ArrowLongDownIcon, ArrowLongUpIcon } from "@heroicons/react/24/solid";
import { SelectFields } from "../../components/select/SelectFields";
import Button from "../../components/button/Button";

export type Action = {
  button: any;
  onClick: () => void;
};

export type RowData = {
  [key: string]: string | number | any | JSX.Element;
};

export type SortableRowData = {
  [key: string]: string | number | boolean;
};

export type GridCol = {
  field: string;
  headerName: string | JSX.Element;
  type?: "number" | "string";
  style: string;
  sort?: boolean;
};

export type GridRow = {
  rowClickHandler?: () => void | Promise<void>;
  rowData: RowData;
  sortableRowData?: SortableRowData;
  style?: string;
};

export type PageItem = {
  perPage: number;
  range: number;
  margin: number;
};

export type selectNumber = {
  id: number;
  name: string;
};

export type TableProps = {
  gridCol: GridCol[];
  gridRow: GridRow[];
  defaultSortKey?: GridCol["field"] | undefined;
  defaultSortOrder?: SortOrder | undefined;
  sort?: boolean;
  header?: boolean;
  footer?: boolean;
  height?: string;
  limit?: number;
  divider?: boolean;
  gridRowLength?: number;
  pagingFunc?: (ver: number) => void;
  selectNumberList?: selectNumber[];
  setSelectNumber?: Function;
  setSortList?: Function;
  openDeleteDialog?: () => void;
  checkedList?: string[];
  setChangedCurrentPage? : Function;
  deleteCheckFlag? : boolean;
  onHeaderClick?: (field: string) => void;
  onSort?: () => void;
  onCustomSort?: (key: string, order: SortOrder) => void;
  displayListValue?: number;
  firstPageFlg?: boolean;
  setFirstPageFlg?: Function;
  setTableResetSort?: (resetFn: () => void) => void;
  minWidth?: string;
  sortKeyList?: {key: string, sort: string}[];
  lineHeight?: string;
  currentPage?: number;
  setCurrentPage?: React.Dispatch<React.SetStateAction<number>>;
  fixedHeight? : boolean;
  additionalContent?: React.ReactNode;
};

type SortOrder = "asc" | "desc";

const TableList = ({
  gridCol,
  gridRow,
  defaultSortKey,
  defaultSortOrder,
  sort = true,
  header = false,
  footer = true,
  divider = false,
  height = "",
  limit = 10,
  gridRowLength = 0,
  pagingFunc,
  selectNumberList,
  setSelectNumber,
  setSortList,
  openDeleteDialog,
  checkedList,
  setChangedCurrentPage,
  onCustomSort,
  displayListValue,
  firstPageFlg = false,
  setFirstPageFlg,
  setTableResetSort,
  minWidth,
  sortKeyList,
  lineHeight,
  currentPage: parentCurrentPage,
  setCurrentPage: parentSetCurrentPage,
  fixedHeight = false,
  additionalContent
}: TableProps) => {

  const [sortKey, setSortKey] = useState<string>(defaultSortKey ?? '');
  const [sortOrder, setSortOrder] = useState<SortOrder>(defaultSortOrder ?? 'asc');
  const [sliceStart, setSliceStart] = useState(0);
  const [sliceEnd, setSliceEnd] = useState(0);
  const [internalCurrentPage, internalSetCurrentPage] = useState(1);
  const currentPage = parentCurrentPage ?? internalCurrentPage;
  const setCurrentPage = parentSetCurrentPage ?? internalSetCurrentPage;

  const sortedData = useCallback(() => {
    if (onCustomSort) {
      return gridRow;
    }
    if (!sortKey) {
      return gridRow;
    }
    if(sortKeyList && sortKeyList.length > 0){
      // ソートが複数条件の場合
      return gridRow.sort((a, b) => {
        for (let i = 0; i < sortKeyList.length; i++) {
          const currentRowData = a.rowData[sortKeyList[i].key];
          const previewRowData = b.rowData[sortKeyList[i].key];
          if (sortKeyList[i].sort === "desc") {
            if (currentRowData < previewRowData) return 1;
            if (currentRowData > previewRowData) return -1;
          }else{
            if (currentRowData > previewRowData) return 1;
            if (currentRowData < previewRowData) return -1;
          }
        }
        return 0
      });
    }
    return gridRow.sort((a, b) => {
      const currentRowData = a.sortableRowData !== undefined && a.sortableRowData[sortKey] !== undefined
        ? a.sortableRowData[sortKey]
        : a.rowData[sortKey];
      const previewRowData = b.sortableRowData !== undefined && b.sortableRowData[sortKey] !== undefined
        ? b.sortableRowData[sortKey]
        : b.rowData[sortKey];
  
      if (sortOrder === "desc") {
        return currentRowData < previewRowData ? 1 : -1;
      }
      return currentRowData > previewRowData ? 1 : -1;
    });
  }, [gridRow, sortKey, sortOrder, onCustomSort]);

  const changeSort = (key: string) => {
    const newSortOrder = key === sortKey
      ? sortOrder === "asc"
        ? "desc"
        : "asc"
      : "desc";
  
    setSortKey(key);
    setSortOrder(newSortOrder);
  
    if (onCustomSort) {
      onCustomSort(key, newSortOrder);
    } 
  };

  const [offset, setOffset] = useState(0);

  const pages = {
    perPage: (!header && !footer) ? 0 : limit,
    range: 5,
    margin: 2,
  };

  // 最初に全データ取得してからページ分割する画面の場合、データの長さを変数に格納
  if (gridRowLength === 0) {
    gridRowLength = gridRow.length;
  }

  const perPage = pages.perPage > 0 ? pages.perPage : gridRowLength;
  const [totalPage, setTotalPage] = useState(Math.ceil(gridRowLength / perPage))
  const cellNumber = 5;

  const handlePageChange = (page: number) => {
    setOffset((page - 1) * perPage);
    // ページ件数を親コンポーネントへ渡す
    if (pagingFunc !== undefined) {
      pagingFunc(page);
    }
  };

  // 表示ページ分のデータのみ取得した場合は分割する必要がないため、関数が存在しているかどうかで分岐させる
  const sortedData_ = (pagingFunc !== undefined) ? sortedData() : sortedData().slice(offset, offset + perPage);
  useEffect(() => {
    if (setSortList != undefined) {
      setSortList(sortedData())
    }
    if(setChangedCurrentPage != undefined){
      setChangedCurrentPage(currentPage)
    }
  }, [currentPage,gridRowLength]);

  // 検索時と表示件数変更時に1ページ目を表示する
  useEffect(() => {
    if(firstPageFlg) {
      setCurrentPage(1)
      setFirstPageFlg!(false)
    }
    setTotalPage(Math.ceil(gridRowLength / perPage))
  }, [gridRow])

  useEffect(() => {
    if (setTableResetSort) {
      setTableResetSort(() => () => {
        setSortKey(''); 
        setSortOrder("desc");
      });
    }
  }, []);

  // 最小幅の指定
  const styleMinWidth = () => {
    if(minWidth !== undefined){
      return minWidth;
    }else{
      return "min-w-fit";
    }
  }
  // テーブルにスクロール表示を指定
  const styleOverFlow = () => {
    if(minWidth !== undefined){
      return "overflow-auto";
    }else{
      return "overflow-hidden";
    }
  }

  return (
    <>  
      {header && (
        <div className="min-w-full px-[3px]">
          <div className={`${divider ? "divide-x divide-gray-400" : ''} pb-4`}>
            {setSelectNumber && openDeleteDialog && checkedList ? (
              <>
                <div className="flex justify-between">
                  <div className="flex items-center whitespace-nowrap">
                    <Button
                      children={"削除"}
                      size={"sm"}
                      onClick={openDeleteDialog}
                      disabled={checkedList.length > 0 ? (false) : (true)}
                      color={checkedList.length > 0 ? undefined : "black"}
                    />
                  </div>
                  <div className="flex">
                    <div className="flex items-center mr-2">
                      <SelectFields
                        id="selectNumberList"
                        color="indigo"
                        className={"h-10 w-20"}
                        selectItems={selectNumberList}
                        onChange={(e: any) => setSelectNumber ?
                          isNumber(e.target.value) ? setSelectNumber(Number(e.target.value)) : setSelectNumber(e.target.value)
                          : ""}
                        />
                      <h3>
                        件
                      </h3>
                    </div>
                    <Pagination
                      className={'pl-1'}
                      pageCount={totalPage}
                      onChange={handlePageChange}
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      sliceStart={sliceStart}
                      setSliceStart={setSliceStart}
                      sliceEnd={sliceEnd}
                      setSliceEnd={setSliceEnd}
                      cellNumber={cellNumber}
                      firstPageFlg={firstPageFlg}
                    ></Pagination>
                  </div>
                </div>
              </>
              ) : (
              <>
                {setSelectNumber ? (
                  <>
                    <div className={`flex ${additionalContent ? "justify-between" : "justify-end"}`}>
                      {additionalContent
                        ? additionalContent
                        : <></>
                      }
                      <div className="flex">
                        <div className="flex items-center mr-2">
                          <SelectFields
                            id="selectNumberList"
                            color="indigo"
                            className={"h-10 w-20"}
                            selectItems={selectNumberList}
                            onChange={(e: any) => setSelectNumber ?
                              isNumber(e.target.value) ? setSelectNumber(Number(e.target.value)) : setSelectNumber(e.target.value)
                              : ""}
                            />
                          <h3>
                            件
                          </h3>
                        </div>
                        <Pagination
                          className={'pl-1'}
                          pageCount={totalPage}
                          onChange={handlePageChange}
                          currentPage={currentPage}
                          setCurrentPage={setCurrentPage}
                          sliceStart={sliceStart}
                          setSliceStart={setSliceStart}
                          sliceEnd={sliceEnd}
                          setSliceEnd={setSliceEnd}
                          cellNumber={cellNumber}
                          firstPageFlg={firstPageFlg}
                        ></Pagination>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-end">
                      <Pagination
                        className={'pl-1'}
                        pageCount={totalPage}
                        onChange={handlePageChange}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        sliceStart={sliceStart}
                        setSliceStart={setSliceStart}
                        sliceEnd={sliceEnd}
                        setSliceEnd={setSliceEnd}
                        cellNumber={cellNumber}
                        firstPageFlg={firstPageFlg}
                      ></Pagination>
                    </div>
                  </>)
                }
              </>
            )}
          </div>
        </div>
      )}
      <div className="flex flex-col">
        {/* <Filter></Filter> */}
        <div>
          <div className={`inline-block w-full ${styleMinWidth()} pb-1 px-1 align-middle`}>
            <div className={(fixedHeight ? 'max-h-96 overflow-y-auto' :  `relative ${styleOverFlow()}`) + ' shadow ring-1 ring-black ring-opacity-5 md:rounded-lg'}>
              <table className={`min-w-full table-fixed divide-y divide-gray-${divider ? "400" : "300"}`}>
                <thead className={fixedHeight ? "bg-gray-50 sticky top-0 z-10" : "bg-gray-50"}>
                  <tr className={`${divider ? "divide-x divide-gray-400" : ''}`}>
                    {gridCol.map((item, index) => {
                      const colSort = item.sort ?? true;
                      return (
                        <th
                          scope="col"
                          className={`whitespace-nowrap px-3 relative ${lineHeight? lineHeight : "py-3.5"} text-left text-sm font-semibold ${item.style}`}
                          key={`th-${index}-${item.field}`}
                        >
                          {sort && colSort ? (
                            <SortButton
                              label={item.headerName}
                              columnKey={item.field}
                              onClick={() => changeSort(item.field)}
                              {...{
                                sortOrder,
                                sortKey,
                              }}
                            />
                          ) : item.headerName}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className={`divide-y divide-gray-${divider ? "400" : "300"} bg-white`}>
                  {sortedData_
                    .map((item, index) => {
                      return (
                        <tr
                          key={`tr-${index}`}
                          className={`${divider ? "divide-x divide-gray-400" : ''} ${index % 2 === 0 ? '' : "bg-gray-50"}`}
                        >
                          {gridCol.map((col) => {
                            return (
                              <>
                              {col.style == "text-right" ? (
                                <>
                                <td
                                  className={`whitespace-nowrap text-right px-3 ${lineHeight? lineHeight : "py-4"} text-sm text-gray-500 ${item.style}`}
                                  key={`td-${index}-${col.field}`}
                                >
                                  {item.rowData[col.field]}
                                </td>
                                </>
                              ):(
                                <>
                                <td
                                  className={`whitespace-nowrap text-left px-3 ${lineHeight? lineHeight : "py-4"} text-sm text-gray-500 ${item.style}`}
                                  key={`td-${index}-${col.field}`}
                                >
                                  {item.rowData[col.field]}
                                </td>
                                </>
                              )}
                              </>
                            );
                          })}
                        </tr>
                      );
                    })}
                </tbody>
                {footer && (
                  <tfoot>
                    <tr>
                      <td colSpan={gridCol.length} className="text-right">
                        <Pagination
                          className={'px-3 py-3 bg-white border-t justify-end'}
                          pageCount={totalPage}
                          onChange={handlePageChange}
                          currentPage={currentPage}
                          setCurrentPage={setCurrentPage}
                          sliceStart={sliceStart}
                          setSliceStart={setSliceStart}
                          sliceEnd={sliceEnd}
                          setSliceEnd={setSliceEnd}
                          cellNumber={cellNumber}
                          firstPageFlg={firstPageFlg}
                        ></Pagination>
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
      </div >
    </>
  );
};

const SortButton = ({
  label,
  sortOrder,
  columnKey,
  sortKey,
  onClick,
}: {
  label: string | JSX.Element;
  sortOrder: SortOrder;
  columnKey: string;
  sortKey: string;
  onClick: MouseEventHandler<HTMLButtonElement>;
}) => {
  return (
    <button
      onClick={onClick}
      className={`${sortKey === columnKey && sortOrder === "desc"
        ? "sort-button sort-reverse "
        : "sort-button"
        }`}
    >
      <div className="group inline-flex ">
        {label}
        <span
          className={`ml-2 flex-none rounded  group-hover:text-gray-400 ${sortKey === columnKey ? "text-gray-70" : "text-gray-50 "
            }`}
        >
          {sortOrder === "asc" ? (
            <ArrowLongUpIcon className="h-5 w-5" aria-hidden="true" />
          ) : (
            <ArrowLongDownIcon className="h-5 w-5" aria-hidden="true" />
          )}
        </span>
      </div>
    </button>
  );
}

// 数値判定
function isNumber(val: any) {
  let result = false;
  if (typeof val === "number") {
    // 数値型
    result = true;

  } else if (typeof val === "string" && val.length !== 0) {
    // 文字列は数値型に変換（空文字は 0に変換されるため除外）
    const convValue: number = Number(val);
    if (!Number.isNaN(convValue)) {
      // 数値変換可能
      result = true;
    }
  }
  return result;
}

export default TableList;
